import { forwardRef, useId, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

import type { LinkTarget } from '@utils/embeds/embedConfig'
import { eventDates } from '@utils/embeds/eventDates'
import type { PublicEvent } from '@utils/embeds/publicEvent'
import { CoverageBar } from './CoverageBar'
import { EventPageLink, EventTitleLink } from './EventPageLink'

const t = scopedTranslation('embeds.events.map')

type SummaryProps = {
  event: PublicEvent
  links: LinkTarget
  rsvpLabel: string
  showCoverage: boolean
  titleIn?: HTMLElement
}

function coverageOf(event: PublicEvent, showCoverage: boolean) {
  const spots = event.spots.self_sign_up
  const shown = showCoverage && spots.total > 0
  return { spots, shown, full: shown && spots.filled >= spots.total }
}

function SummaryTitle({
  event,
  links,
  titleIn,
  className,
}: Pick<SummaryProps, 'event' | 'links' | 'titleIn'> & { className?: string }) {
  const title = (
    <EventTitleLink event={event} links={links}>
      <p className={cn('tw:m-0 tw:text-sm tw:font-semibold tw:text-gray-900', className)}>{event.name}</p>
    </EventTitleLink>
  )
  return titleIn ? createPortal(title, titleIn) : title
}

export function EventSummary({
  event,
  links,
  rsvpLabel,
  showCoverage,
  locale,
  titleIn,
  titleClassName,
  children,
}: SummaryProps & { locale: string; titleClassName?: string; children?: ReactNode }) {
  const dates = eventDates(event, locale)
  const coverage = coverageOf(event, showCoverage)

  return (
    <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-1.5 tw:text-gray-900">
      <SummaryTitle event={event} links={links} titleIn={titleIn} className={titleClassName} />
      {dates && <p className="tw:m-0 tw:text-xs tw:text-gray-700">{dates.when}</p>}
      {children}
      {coverage.shown && <CoverageBar filled={coverage.spots.filled} total={coverage.spots.total} />}
      <div className="tw:mt-auto tw:flex tw:pt-1">
        <EventPageLink event={event} links={links} rsvpLabel={rsvpLabel} full={coverage.full} />
      </div>
    </div>
  )
}

type PinCalloutProps = Omit<SummaryProps, 'titleIn'> & {
  locale: string
  onClose: () => void
}

export const PinCallout = forwardRef<HTMLDivElement, PinCalloutProps>(function PinCallout(
  { event, links, rsvpLabel, showCoverage, locale, onClose },
  ref
) {
  const titleId = useId()
  const dates = eventDates(event, locale)

  return (
    <div
      ref={ref}
      role="dialog"
      aria-labelledby={titleId}
      onKeyDown={(keyEvent) => keyEvent.key === 'Escape' && onClose()}
      className="tw:w-64 tw:max-w-[calc(100vw-3rem)] tw:rounded-[var(--embed-radius-sm,0.5rem)] tw:border tw:border-gray-200 tw:bg-white tw:p-3 tw:text-gray-900 tw:shadow-lg tw:motion-safe:animate-in tw:motion-safe:fade-in-0 tw:motion-safe:zoom-in-95"
    >
      <div className="tw:flex tw:items-start tw:gap-2">
        <div id={titleId} className="tw:min-w-0 tw:flex-1">
          <SummaryTitle event={event} links={links} className="tw:line-clamp-2" />
        </div>
        <button
          type="button"
          aria-label={t('close')}
          onClick={onClose}
          className="tw:-mr-1 tw:-mt-1 tw:flex tw:h-7 tw:w-7 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-gray-500 tw:hover:bg-gray-100 tw:hover:text-gray-700 tw:focus-visible:outline tw:focus-visible:outline-2 tw:focus-visible:outline-[color:var(--embed-brand-ink,#111827)]"
        >
          <Icon type="x-close" className="tw:h-4 tw:w-4" />
        </button>
      </div>
      {dates && <p className="tw:m-0 tw:mt-0.5 tw:truncate tw:text-xs tw:text-gray-600">{dates.when}</p>}
      <div className="tw:mt-3 tw:flex">
        <EventPageLink event={event} links={links} rsvpLabel={rsvpLabel} full={coverageOf(event, showCoverage).full} />
      </div>
    </div>
  )
})
